import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  getAdminRecognitions, 
  updateRecognition, 
  deleteRecognition 
} from "@/lib/recognitions.functions";
import { Button } from "@/components/ui/button";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Eye, 
  MoreHorizontal, 
  Loader2, 
  Trophy, 
  Image as ImageIcon 
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export const Route = createFileRoute("/_admin/admin/cms/")({
  component: AdminRecognitionsPage,
});

function AdminRecognitionsPage() {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecognition, setEditingRecognition] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const { data: recognitions, isLoading } = useQuery({
    queryKey: ["admin-recognitions"],
    queryFn: () => getAdminRecognitions(),
  });

  const upsertMutation = useMutation({
    mutationFn: (data: any) => updateRecognition({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-recognitions"] });
      queryClient.invalidateQueries({ queryKey: ["recognitions"] });
      toast.success(editingRecognition ? "Recognition updated" : "Recognition added");
      setIsDialogOpen(false);
      setEditingRecognition(null);
    },
    onError: (error: any) => {
      toast.error(error.message || "Something went wrong");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (data: any) => deleteRecognition({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-recognitions"] });
      queryClient.invalidateQueries({ queryKey: ["recognitions"] });
      toast.success("Recognition deleted");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to delete");
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      id: editingRecognition?.id,
      title: formData.get("title") as string,
      organization: formData.get("organization") as string,
      year: parseInt(formData.get("year") as string),
      description: formData.get("description") as string,
      image_url: formData.get("image_url") as string,
      is_active: formData.get("is_active") === "on",
      display_order: parseInt(formData.get("display_order") as string || "0"),
    };
    upsertMutation.mutate(data);
  };

  const handleEdit = (recognition: any) => {
    setEditingRecognition(recognition);
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this recognition?")) {
      deleteMutation.mutate({ id });
    }
  };

  const handlePreview = (url: string) => {
    setPreviewUrl(url);
    setIsPreviewOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Recognition & Awards</h1>
          <p className="text-sm text-muted-foreground">
            Manage certificates and awards displayed on the public website.
          </p>
        </div>
        <Button onClick={() => { setEditingRecognition(null); setIsDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" /> Add Award
        </Button>
      </div>

      <div className="rounded-md border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Image</TableHead>
              <TableHead>Award Details</TableHead>
              <TableHead>Year</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : recognitions?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  No awards found.
                </TableCell>
              </TableRow>
            ) : (
              recognitions?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div 
                      className="h-10 w-10 overflow-hidden rounded border bg-muted cursor-pointer hover:opacity-80"
                      onClick={() => handlePreview(item.image_url)}
                    >
                      <img src={item.image_url} alt="" className="h-full w-full object-cover" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{item.title}</div>
                    <div className="text-xs text-muted-foreground">{item.organization}</div>
                  </TableCell>
                  <TableCell>{item.year}</TableCell>
                  <TableCell>
                    <span className={cn(
                      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                      item.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                    )}>
                      {item.is_active ? "Active" : "Disabled"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(item)}>
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handlePreview(item.image_url)}>
                          <Eye className="mr-2 h-4 w-4" /> Preview
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-destructive" 
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{editingRecognition ? "Edit Award" : "Add New Award"}</DialogTitle>
              <DialogDescription>
                Fill in the details for the certificate or recognition.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="title">Award Title</Label>
                <Input 
                  id="title" 
                  name="title" 
                  defaultValue={editingRecognition?.title} 
                  required 
                  placeholder="e.g. Restaurant Guru 2026"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="organization">Issuing Organization</Label>
                <Input 
                  id="organization" 
                  name="organization" 
                  defaultValue={editingRecognition?.organization} 
                  required 
                  placeholder="e.g. Restaurant Guru"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="year">Year</Label>
                  <Input 
                    id="year" 
                    name="year" 
                    type="number" 
                    defaultValue={editingRecognition?.year || new Date().getFullYear()} 
                    required 
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="display_order">Display Order</Label>
                  <Input 
                    id="display_order" 
                    name="display_order" 
                    type="number" 
                    defaultValue={editingRecognition?.display_order || 0} 
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Short Description (Optional)</Label>
                <Textarea 
                  id="description" 
                  name="description" 
                  defaultValue={editingRecognition?.description} 
                  placeholder="Recognised for our commitment..."
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="image_url">Certificate Image URL</Label>
                <div className="flex gap-2">
                  <Input 
                    id="image_url" 
                    name="image_url" 
                    defaultValue={editingRecognition?.image_url} 
                    required 
                    placeholder="/assets/award.png or https://..."
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={() => toast.info("Use Media Library to upload and copy URL")}
                  >
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch id="is_active" name="is_active" defaultChecked={editingRecognition?.is_active ?? true} />
                <Label htmlFor="is_active">Active / Visible on website</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={upsertMutation.isPending}>
                {upsertMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingRecognition ? "Save Changes" : "Create Award"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Certificate Preview</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center bg-muted rounded-lg p-4">
            <img src={previewUrl} alt="Preview" className="max-h-[60vh] object-contain" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(" ");
}
